import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Lock, Users, Code, Megaphone, Shield, AlertTriangle, Award, Coins } from 'lucide-react';

const Tokenomics = () => {
  const treasuries = [
    {
      name: 'Community Treasury',
      amount: '9,000,000',
      percentage: 30,
      address: '0xde525b1040ee5b8991b9edd5e0705016ad6d927c',
      icon: Users,
      description: 'Main community fund: incentives, staking, governance, rewards and participation programs.',
      multisig: '3/5'
    },
    {
      name: 'Development Fund',
      amount: '6,000,000',
      percentage: 20,
      address: '0x2e4689f0e7fa0ce3990ccd790bb4708d2acea0e9',
      icon: Code,
      description: 'Resources for protocol technical development, node maintenance, explorer, AVO Station and tools.',
      multisig: '3/5'
    },
    {
      name: 'Main Treasury',
      amount: '4,500,000',
      percentage: 15,
      address: '0x88ebec48b5d1f7a91d71e2d5bb2f4b504ea8569c',
      icon: PieChart,
      description: 'Strategic reserve and general operations for the ecosystem.',
      multisig: '4/7'
    },
    {
      name: 'Marketing Treasury',
      amount: '3,000,000',
      percentage: 10,
      address: '0xc8084330dd6624670466848d1ff0ca5452759374',
      icon: Megaphone,
      description: 'Promotion, partnerships, branding, media presence and global growth.',
      multisig: '2/4'
    },
    {
      name: 'Security & Emergency Fund',
      amount: '3,000,000',
      percentage: 10,
      address: '0xe932f91cbc06831adc4f7e85335d864d23f2da49',
      icon: Shield,
      description: 'Audits, on-chain security, response to attacks or critical failures.',
      multisig: '3/5'
    },
    {
      name: 'Team Allocation',
      amount: '2,400,000',
      percentage: 8,
      address: '0x9d0d329c2d1acb5cbe1502ca0a98e86eca05632d',
      icon: Award,
      description: 'Long-term incentives and retention for founding team and key collaborators.',
      multisig: '2/3',
      vesting: 'Until Aug 9, 2026'
    },
    {
      name: 'Seed Sale',
      amount: '2,100,000',
      percentage: 7,
      address: '0x8c0491d035071e62101f57f0f4579a67f2ac82af',
      icon: Coins,
      description: 'Initial private sale to obtain liquidity and fund the first project milestones.',
      multisig: '5/7'
    }
  ];

  const totalSupply = 30000000;

  return (
    <div className="min-h-screen bg-black text-white py-20">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="container mx-auto px-6 mb-20"
      >
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            Tokenomics
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transparent distribution of 30,000,000 AVO with multisig governance and responsible vesting.
          </p>
        </div>

        {/* Total Supply Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-black border-2 border-white p-12 max-w-4xl mx-auto mb-16"
        >
          <div className="text-center">
            <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Total Supply</p>
            <h2 className="text-7xl font-bold mb-2 text-white">30,000,000</h2>
            <p className="text-3xl text-white tracking-widest">AVO</p>
          </div>
        </motion.div>

        {/* Distribution Bars - Modern Visualization */}
        <div className="max-w-5xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Token Distribution</h3>
          <div className="space-y-3">
            {treasuries.map((treasury, index) => {
              const Icon = treasury.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-black border-2 border-white p-4 hover:border-blue-400 transition-all"
                >
                  <div className="flex items-center gap-4 mb-2">
                    <Icon className="w-5 h-5 text-white" />
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline mb-1">
                        <h4 className="font-bold text-white">{treasury.name}</h4>
                        <span className="text-sm text-gray-400">{treasury.amount} AVO</span>
                      </div>
                      {/* Progress bar */}
                      <div className="relative w-full h-2 bg-gray-950 border border-white overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${treasury.percentage}%` }}
                          transition={{ delay: 0.2 * index, duration: 0.8 }}
                          className="absolute h-full bg-white"
                        />
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-white min-w-[60px] text-right">{treasury.percentage}%</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Treasury Cards */}
      <section className="container mx-auto px-6 mb-20">
        <h2 className="text-4xl font-bold text-center mb-12 text-white">Treasury Details</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {treasuries.map((treasury, index) => {
            const Icon = treasury.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-black border-2 border-white p-6 hover:border-purple-400 transition-all group"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-black border-2 border-white group-hover:border-purple-400 transition-all">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1 text-white">{treasury.name}</h3>
                    <p className="text-2xl font-bold text-white">{treasury.amount}</p>
                    <p className="text-sm text-gray-400">{treasury.percentage}% of supply</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-300 mb-4">{treasury.description}</p>
                
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-white" />
                    <span className="text-gray-400">Multisig: {treasury.multisig}</span>
                  </div>
                  {treasury.vesting && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-white" />
                      <span className="text-gray-400">Vesting: {treasury.vesting}</span>
                    </div>
                  )}
                  <div className="bg-gray-950 border border-white p-2 mt-2 font-mono text-[10px] break-all text-gray-300">
                    {treasury.address}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Why This Distribution */}
      <section className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-black border-2 border-white p-8 md:p-12"
        >
          <h2 className="text-3xl font-bold mb-6 text-center text-white">Why This Distribution?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-3">◉ Community First</h3>
              <p className="text-gray-300">
                30% allocated to the community demonstrates our commitment to decentralization and 
                organic ecosystem growth.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-3">◉ Multisig Governance</h3>
              <p className="text-gray-300">
                All treasuries are protected with multisig, requiring multiple approvals 
                for any transaction, ensuring transparency.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-3">◉ Responsible Vesting</h3>
              <p className="text-gray-300">
                Team allocation (8%) is under vesting until 2026, aligning long-term incentives 
                with protocol success. Seed sale (7%) provides initial funding for development.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Tokenomics;
